import { isDefinedError, safe } from "@orpc/client"
import { createFileRoute, Link } from "@tanstack/react-router"
import { Pencil, Trash2 } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"
import {
	ID_LINK_CLASS,
	Money,
	QueryErrorLine,
	SkeletonRows,
} from "@/components/accounting/accounting-ui"
import { EMPTY_VALUE } from "@/components/accounting/reconciliation-format"
import { PageHeader, Panel, Section, SectionStack } from "@/components/layout"
import { Button } from "@/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { useVatDeclarations } from "@/queries/marketplace/use-vat-declarations"
import {
	type FilingScheme,
	useCreateVatRemittance,
	useDeleteVatRemittance,
	useOutstandingVatSummary,
	useUpdateVatRemittance,
	useVatRemittances,
} from "@/queries/marketplace/use-vat-remittances"

export const Route = createFileRoute("/accounting/vat-remittances/")({
	component: VatRemittancesPage,
})

const SCHEMES: FilingScheme[] = [
	"ioss",
	"uk_omp",
	"eu_oss",
	"fr_domestic",
	"uk_vat",
	"ch_vat",
	"stripe_tax_na",
]

function VatRemittancesPage() {
	const list = useVatRemittances({})
	const declarations = useVatDeclarations({})
	const summary = useOutstandingVatSummary()
	const createMut = useCreateVatRemittance()
	const updateMut = useUpdateVatRemittance()

	const declarationById = React.useMemo(() => {
		const map = new Map<string, { scheme: string; filingPeriod: string }>()
		for (const d of declarations.data?.items ?? []) {
			map.set(d.declarationId, {
				scheme: d.scheme,
				filingPeriod: d.filingPeriod,
			})
		}
		return map
	}, [declarations.data])
	const deleteMut = useDeleteVatRemittance()

	const items = list.data?.items ?? []

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="VAT remittances"
				count={list.data ? items.length : undefined}
				description="Log filings made to tax authorities. Linked automatically to monthly closes by (scheme, period)."
			/>

			<SectionStack>
				<Section label="Outstanding by scheme">
					{summary.isError ? (
						<QueryErrorLine what="outstanding VAT" error={summary.error} />
					) : (
						<Panel>
							{summary.isLoading ? (
								<SkeletonRows rows={4} />
							) : (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Scheme</TableHead>
											<TableHead className="text-right">
												Collected (EUR)
											</TableHead>
											<TableHead className="text-right">
												Remitted (EUR)
											</TableHead>
											<TableHead className="text-right">
												Outstanding (EUR)
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{summary.data?.perScheme.map((row) => (
											<TableRow key={row.scheme}>
												<TableCell className="font-mono">
													{row.scheme}
												</TableCell>
												<TableCell className="text-right">
													<Money cents={row.collectedEur} currency="EUR" />
												</TableCell>
												<TableCell className="text-right text-muted-foreground">
													<Money cents={row.remittedEur} currency="EUR" />
												</TableCell>
												<TableCell className="text-right font-medium">
													<Money
														cents={row.outstandingEur}
														currency="EUR"
														muteZero
													/>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</Panel>
					)}
				</Section>

				<Section label="Logged filings">
					{list.isError ? (
						<QueryErrorLine what="the filings" error={list.error} />
					) : list.isLoading ? (
						<Panel>
							<SkeletonRows rows={4} />
						</Panel>
					) : items.length === 0 ? (
						<p className="text-muted-foreground">No filings logged yet.</p>
					) : (
						<Panel>
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Filed</TableHead>
										<TableHead>Region</TableHead>
										<TableHead>Scheme</TableHead>
										<TableHead>Period</TableHead>
										<TableHead>Declaration</TableHead>
										<TableHead>Reference</TableHead>
										<TableHead className="text-right">Amount EUR</TableHead>
										<TableHead className="text-right">Local</TableHead>
										<TableHead />
									</TableRow>
								</TableHeader>
								<TableBody>
									{items.map((r) => {
										const declaration = r.declarationId
											? declarationById.get(r.declarationId)
											: undefined
										return (
											<TableRow key={r.remittanceId}>
												<TableCell className="tabular-nums">
													{new Date(r.remittedAt).toLocaleDateString()}
												</TableCell>
												<TableCell className="font-mono">{r.region}</TableCell>
												<TableCell className="font-mono">{r.scheme}</TableCell>
												<TableCell className="whitespace-nowrap font-mono">
													{new Date(r.filingPeriodFrom)
														.toISOString()
														.slice(0, 7)}{" "}
													→{" "}
													{new Date(r.filingPeriodTo).toISOString().slice(0, 7)}
												</TableCell>
												<TableCell>
													{declaration && r.declarationId ? (
														<Link
															to="/accounting/vat-declarations/$declarationId"
															params={{ declarationId: r.declarationId }}
															className={ID_LINK_CLASS}
														>
															{declaration.scheme} {declaration.filingPeriod}
														</Link>
													) : (
														<span className="text-meta">{EMPTY_VALUE}</span>
													)}
												</TableCell>
												<TableCell className="font-mono">
													{r.filingReference}
												</TableCell>
												<TableCell className="text-right">
													<Money cents={r.amountEur} currency="EUR" />
												</TableCell>
												<TableCell className="text-right text-muted-foreground">
													<Money cents={r.amountLocal} currency={r.currency} />
												</TableCell>
												<TableCell className="text-right">
													<div className="flex justify-end gap-1">
														<EditRemittanceSchemeDialog
															remittanceId={r.remittanceId}
															scheme={r.scheme}
															onSave={(scheme) =>
																updateMut.mutateAsync({
																	remittanceId: r.remittanceId,
																	scheme,
																})
															}
														/>
														<Button
															variant="ghost"
															size="icon"
															className="size-[26px]"
															aria-label="Delete remittance"
															onClick={() => {
																if (
																	confirm(
																		"Delete this remittance? Outstanding totals will adjust.",
																	)
																) {
																	deleteMut.mutate(r.remittanceId)
																}
															}}
														>
															<Trash2 />
														</Button>
													</div>
												</TableCell>
											</TableRow>
										)
									})}
								</TableBody>
							</Table>
						</Panel>
					)}
				</Section>

				<LogRemittanceForm
					onSubmit={async (input) => {
						const [error] = await safe(createMut.mutateAsync(input))
						if (isDefinedError(error)) {
							const e = error as { code: string }
							toast.error(`Could not save: ${e.code}`)
							return false
						}
						if (error) {
							toast.error("Save failed.")
							return false
						}
						toast.success("Filing logged.")
						return true
					}}
					busy={createMut.isPending}
				/>
			</SectionStack>
		</div>
	)
}

function EditRemittanceSchemeDialog({
	remittanceId,
	scheme,
	onSave,
}: {
	remittanceId: string
	scheme: FilingScheme
	onSave: (scheme: FilingScheme) => Promise<unknown>
}) {
	const [open, setOpen] = React.useState(false)
	const [nextScheme, setNextScheme] = React.useState<FilingScheme>(scheme)
	const [saving, setSaving] = React.useState(false)

	const handleSave = async () => {
		setSaving(true)
		try {
			await onSave(nextScheme)
			toast.success("Remittance relabelled.")
			setOpen(false)
		} catch {
			toast.error("Could not save the scheme.")
		} finally {
			setSaving(false)
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				setOpen(next)
				if (next) setNextScheme(scheme)
			}}
		>
			<DialogTrigger asChild>
				<Button variant="ghost" size="sm">
					<Pencil className="h-4 w-4" />
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>Relabel remittance {remittanceId}</DialogTitle>
				</DialogHeader>
				<Field label="Scheme">
					<Select
						value={nextScheme}
						onValueChange={(v) => setNextScheme(v as FilingScheme)}
					>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{SCHEMES.map((s) => (
								<SelectItem key={s} value={s}>
									{s}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</Field>
				<DialogFooter>
					<Button variant="outline" onClick={() => setOpen(false)}>
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={saving}>
						Save
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function LogRemittanceForm({
	onSubmit,
	busy,
}: {
	onSubmit: (input: {
		scheme: FilingScheme
		fromYear: number
		fromMonth: number
		toYear: number
		toMonth: number
		filingReference: string
		amountEur: number
		amountLocal: number
		currency: string
		remittedAt: Date
	}) => Promise<boolean>
	busy: boolean
}) {
	const now = new Date()
	const [scheme, setScheme] = React.useState<FilingScheme>("ioss")
	const [fromYear, setFromYear] = React.useState(now.getUTCFullYear())
	const [fromMonth, setFromMonth] = React.useState(now.getUTCMonth() + 1)
	const [toYear, setToYear] = React.useState(now.getUTCFullYear())
	const [toMonth, setToMonth] = React.useState(now.getUTCMonth() + 1)
	const [filingReference, setFilingReference] = React.useState("")
	const [amountEur, setAmountEur] = React.useState("")
	const [amountLocal, setAmountLocal] = React.useState("")
	const [currency, setCurrency] = React.useState("EUR")
	const [remittedAt, setRemittedAt] = React.useState(
		now.toISOString().slice(0, 10),
	)

	const handle = async () => {
		if (!filingReference.trim()) {
			toast.error("Filing reference is required.")
			return
		}
		const ok = await onSubmit({
			scheme,
			fromYear,
			fromMonth,
			toYear,
			toMonth,
			filingReference: filingReference.trim(),
			amountEur: Math.round(Number(amountEur) * 100),
			amountLocal: Math.round(Number(amountLocal) * 100),
			currency,
			remittedAt: new Date(remittedAt),
		})
		if (ok) {
			setFilingReference("")
			setAmountEur("")
			setAmountLocal("")
		}
	}

	return (
		<Section id="log-filing" label="Log a filing" className="scroll-mt-6">
			<div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-3">
				<Field label="Scheme">
					<Select
						value={scheme}
						onValueChange={(v) => setScheme(v as FilingScheme)}
					>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{SCHEMES.map((s) => (
								<SelectItem key={s} value={s}>
									{s}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</Field>
				<Field label="From (month/year)">
					<div className="flex gap-2">
						<MonthSelect value={fromMonth} onChange={setFromMonth} />
						<YearInput value={fromYear} onChange={setFromYear} />
					</div>
				</Field>
				<Field label="To (month/year)">
					<div className="flex gap-2">
						<MonthSelect value={toMonth} onChange={setToMonth} />
						<YearInput value={toYear} onChange={setToYear} />
					</div>
				</Field>
				<Field label="Filing reference">
					<Input
						value={filingReference}
						onChange={(e) => setFilingReference(e.target.value)}
						placeholder="e.g. IOSS-2026-04-FR"
					/>
				</Field>
				<Field label="Amount (EUR)">
					<Input
						type="number"
						step="0.01"
						value={amountEur}
						onChange={(e) => setAmountEur(e.target.value)}
					/>
				</Field>
				<Field label="Amount (local) + ccy">
					<div className="flex gap-2">
						<Input
							type="number"
							step="0.01"
							value={amountLocal}
							onChange={(e) => setAmountLocal(e.target.value)}
						/>
						<Input
							className="w-20"
							value={currency}
							onChange={(e) => setCurrency(e.target.value.toUpperCase())}
						/>
					</div>
				</Field>
				<Field label="Remitted at">
					<Input
						type="date"
						value={remittedAt}
						onChange={(e) => setRemittedAt(e.target.value)}
					/>
				</Field>
			</div>
			<div>
				<Button onClick={handle} disabled={busy}>
					Log filing
				</Button>
			</div>
		</Section>
	)
}

function Field({
	label,
	children,
}: {
	label: string
	children: React.ReactNode
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<Label className="text-xs font-normal text-meta">{label}</Label>
			{children}
		</div>
	)
}

function MonthSelect({
	value,
	onChange,
}: {
	value: number
	onChange: (v: number) => void
}) {
	return (
		<Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
			<SelectTrigger>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
					<SelectItem key={m} value={String(m)}>
						{String(m).padStart(2, "0")}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}

function YearInput({
	value,
	onChange,
}: {
	value: number
	onChange: (v: number) => void
}) {
	return (
		<Input
			type="number"
			min={2020}
			max={2100}
			value={value}
			className="w-24"
			onChange={(e) => onChange(Number(e.target.value))}
		/>
	)
}
